/**
 * ChecklistStatusMenu — Context menu for checklist item status and progress.
 *
 * Triggered by right-click / long-press on the ChecklistIcon.
 * Provides:
 * - Status: Unfinished / Completed / Canceled
 * - Progress: Slider (0–100%) with snap points
 * - UserStatus: Workflow status picker (§21)
 */

import { memo, useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import { ObjectStatus, ChecklistSubType } from "@/db/schema"
import { updateChecklistStatus, updateChecklistProgress } from "@/db/operations"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { Slider } from "@/app/components/ui/slider"
import { UserStatusPicker } from "@/shared/components/UserStatusPicker"
import { useMenuPosition } from "@/shared/hooks/useMenuPosition"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChecklistStatusMenuProps {
  itemId: string
  currentStatus: ObjectStatus
  currentProgress?: number
  /** Current workflow status (string model, §21) */
  currentUserStatus?: string | number | null
  /** Current subType (Item=0, Section=1) */
  currentSubType?: number
  /** Whether this section heading is currently collapsed */
  isCollapsed?: boolean
  /** Anchor position for the menu */
  position: { x: number; y: number }
  onClose: () => void
  /** Toggle between Item and Section */
  onToggleSectionHeading?: (itemId: string) => void
  /** Toggle collapse/expand for section headings */
  onToggleCollapse?: (itemId: string) => void
}

// ---------------------------------------------------------------------------
// Status options
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  {
    status: ObjectStatus.Unfinished,
    label: "Unfinished",
    shortLabel: "Open",
    color: "var(--checkbox-border)",
  },
  {
    status: ObjectStatus.Completed,
    label: "Completed",
    shortLabel: "Done",
    color: "var(--primary)",
  },
  {
    status: ObjectStatus.Canceled,
    label: "Canceled",
    shortLabel: "Cancel",
    color: "var(--foreground)",
  },
] as const

/** Snap to nearest 5% for a neat display value */
function snapProgress(value: number): number {
  return Math.round(value / 5) * 5
}

/**
 * Width of the slider thumb in px — `size-4` on SliderPrimitive.Thumb in
 * `ui/slider.tsx`. The thumb is centred on its value, so it travels over
 * (track width - thumb width); the tick labels below the track use this to
 * land on the same x as the thumb. Keep in sync with the Thumb's size class.
 */
const SLIDER_THUMB_PX = 16

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Position helper — uses shared useMenuPosition hook
// ---------------------------------------------------------------------------

export const ChecklistStatusMenu = memo(function ChecklistStatusMenu({
  itemId,
  currentStatus,
  currentProgress = 0,
  currentUserStatus,
  currentSubType,
  isCollapsed,
  position,
  onClose,
  onToggleSectionHeading,
  onToggleCollapse,
}: ChecklistStatusMenuProps) {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)
  const [sliderValue, setSliderValue] = useState(currentProgress)

  // Close on Escape. Outside-tap dismissal is handled by the backdrop below —
  // a document-level listener would be redundant (and `mousedown` fires too
  // late relative to touch on iOS to reliably absorb the gesture).
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("keydown", handleEscape)
    }
  }, [onClose])

  const handleStatusChange = useCallback(
    (status: ObjectStatus) => {
      updateChecklistStatus(itemId, status)
      onClose()
    },
    [itemId, onClose],
  )

  // Live slider update (visual only)
  const handleSliderChange = useCallback((value: number[]) => {
    setSliderValue(snapProgress(value[0] ?? 0))
  }, [])

  // Commit to DB on release
  const handleSliderCommit = useCallback(
    (value: number[]) => {
      const snapped = snapProgress(value[0] ?? 0)
      updateChecklistProgress(itemId, snapped)
    },
    [itemId],
  )

  // Clamp menu position to viewport
  const menuStyle = useMenuPosition(position, menuRef)

  return createPortal(
    <>
      {/* Backdrop — absorbs the dismiss tap before it reaches whatever is
          underneath (the checklist row, the TipTap editor, etc.). Uses
          pointerdown rather than click/mousedown so the gesture is absorbed
          at the earliest possible point on touch devices. */}
      <div
        data-context-menu-backdrop
        className="fixed inset-0 z-40 pointer-events-auto touch-none"
        // Absorb the press, but do NOT close here. Closing on pointerdown
        // unmounts this backdrop immediately, and the same physical tap then
        // produces a trailing `click` with nothing left to catch it — that
        // click lands on the row underneath and closed the whole task.
        // Reproduced with a real touch on Android: tapping outside dismissed
        // the menu *and* navigated away from the task.
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        // Close on click instead, while the backdrop is still mounted to
        // swallow it. This is the pattern TaskContextMenu already uses.
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onClose()
        }}
      />

      <div
        ref={menuRef}
        className={cn(
          "fixed z-50 w-[260px] pointer-fine:w-[220px] rounded-lg border bg-popover shadow-lg",
          "animate-in fade-in-0 zoom-in-95",
          // max-height comes from useMenuPosition — it knows the real insets
          "flex flex-col overflow-hidden",
        )}
        style={menuStyle}
        role="menu"
        aria-label="Checklist item options"
      >
        {/* Scrollable middle region — everything except the pinned footer */}
        {/* overflow-x-hidden is deliberate: setting only overflow-y makes the
            other axis compute from `visible` to `auto`, which would hand this
            region a horizontal scrollbar the moment any child is too wide. */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-1.5">
          {/* Status section — icon + tiny caption, horizontal */}
          <div className="flex items-stretch justify-around px-1 py-1.5 pointer-fine:py-1">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.status}
                type="button"
                role="menuitem"
                title={t(opt.label)}
                onClick={() => handleStatusChange(opt.status)}
                className={cn(
                  "flex flex-col items-center gap-1 pointer-fine:gap-0.5 rounded-md px-3 py-2 pointer-fine:py-1.5",
                  "hover:bg-accent transition-colors duration-100",
                  currentStatus === opt.status && "bg-accent/60",
                )}
              >
                <DynamicIcon
                  type="project"
                  percentage={opt.status === ObjectStatus.Completed ? 100 : 0}
                  unfinished={0}
                  status={opt.status}
                  size="small"
                  color={opt.color}
                />
                <span className="text-[11px] pointer-fine:text-[10px] leading-tight text-muted-foreground">
                  {t(opt.shortLabel)}
                </span>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="my-1 h-px bg-border" />

          {/* Progress section — slider */}
          <div className="flex items-center justify-between px-2 py-1.5 pointer-fine:py-1">
            <span className="text-sm pointer-fine:text-xs font-medium text-muted-foreground">
              Progress
            </span>
            <span className="text-sm pointer-fine:text-xs tabular-nums font-medium text-foreground">
              {sliderValue}%
            </span>
          </div>
          <div className="px-3 pb-2 pointer-fine:pb-1.5 pt-1 pointer-fine:pt-0.5">
            <Slider
              value={[sliderValue]}
              min={0}
              max={100}
              step={5}
              onValueChange={handleSliderChange}
              onValueCommit={handleSliderCommit}
              aria-label="Progress"
            />
            {/* Tick marks at 0, 25, 50, 75, 100.
                These must line up with the slider thumb, so each label is
                absolutely positioned at the same x the thumb takes for that
                value: the thumb travels over (track width - thumb width) and
                is centred, so value v sits at halfThumb + usable * v/100.

                A flex row with justify-between does NOT work here: it spaces
                the label *boxes* evenly, so every label's centre is inset by
                half its own width — and the labels have different widths
                ("0" vs "100"). Measured on device, that put the "50" label at
                46% of the track, so tapping it produced 46% which step={5}
                rounded down to 45%. That was the reported "50% shows as 45%". */}
            <div className="relative mt-1.5 pointer-fine:mt-1 h-4 pointer-fine:h-3">
              {[0, 25, 50, 75, 100].map((tick) => (
                <button
                  key={tick}
                  type="button"
                  style={{
                    left: `calc(${SLIDER_THUMB_PX / 2}px + (100% - ${SLIDER_THUMB_PX}px) * ${tick / 100})`,
                  }}
                  onClick={() => {
                    setSliderValue(tick)
                    updateChecklistProgress(itemId, tick)
                  }}
                  className={cn(
                    "absolute top-0 -translate-x-1/2",
                    "text-xs pointer-fine:text-[10px] tabular-nums text-muted-foreground/60",
                    "hover:text-foreground transition-colors cursor-pointer",
                    sliderValue === tick && "text-foreground font-medium",
                  )}
                >
                  {tick}
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="my-1 h-px bg-border" />

          {/* UserStatus section — workflow status picker */}
          <UserStatusPicker
            taskId={itemId}
            currentValue={currentUserStatus}
            onClose={onClose}
          />
        </div>

        {/* Pinned footer — always visible, never pushed off-screen by the
            scrollable content above */}
        {/* Plain padding only. useMenuPosition already reserves the bottom
            safe area *outside* the panel, so adding an in-panel safe-area
            spacer on top of this stacked ~50px of dead space under the last
            row — it read as one absurdly tall button and shrank the already
            cramped menu further when the keyboard was up. */}
        <div className="shrink-0 border-t border-border p-1.5">
          {/* Make Heading / Convert to Item toggle */}
          <button
            type="button"
            role="menuitem"
            className={cn(
              "flex w-full items-center gap-2.5 pointer-fine:gap-2 rounded-md px-2.5 pointer-fine:px-2 py-2.5 pointer-fine:py-1.5",
              "text-sm pointer-fine:text-xs text-muted-foreground hover:bg-accent hover:text-foreground",
              "transition-colors duration-100",
            )}
            onClick={() => {
              onToggleSectionHeading?.(itemId)
              onClose()
            }}
          >
            <span className="text-sm font-bold leading-none">H</span>
            <span>
              {currentSubType === ChecklistSubType.Heading
                ? "Convert to Item"
                : "Make Heading"}
            </span>
          </button>

          {/* Collapse/Expand section (only for headings) */}
          {currentSubType === ChecklistSubType.Heading && onToggleCollapse && (
            <button
              type="button"
              role="menuitem"
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5",
                "text-xs text-muted-foreground hover:bg-accent hover:text-foreground",
                "transition-colors duration-100",
              )}
              onClick={() => {
                onToggleCollapse(itemId)
                onClose()
              }}
            >
              <span className={cn("text-sm leading-none transition-transform", isCollapsed && "-rotate-180")}>▾</span>
              <span>{isCollapsed ? "Expand Section" : "Collapse Section"}</span>
            </button>
          )}
        </div>
      </div>
    </>,
    document.body,
  )
})
